import { Component, OnInit, ElementRef, inject, Injector, runInInjectionContext, signal, computed, AfterViewInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ActivityService } from 'src/app/shared/activity.service';

@Component({
	selector: 'app-stories',
	templateUrl: './stories.component.html',
	styleUrls: ['./stories.component.scss'],
	standalone: false // 1. Reverted to traditional NgModule architecture
})
export class StoriesComponent implements OnInit, AfterViewInit {
	// Modern Dependency Injection
	private readonly afs = inject(AngularFirestore);
	private readonly auth = inject(ActivityService);
	public readonly storage = inject(AngularFireStorage);
	private readonly el = inject(ElementRef);
	private readonly location = inject(Location);
	private readonly injector = inject(Injector);

	// Signals for Reactive State
	readonly loading = signal(true);
	readonly tabs = signal<any[][]>([]);
	readonly activeWindow = signal(1);

	// Computed Signals
	readonly tabCount = computed(() => this.tabs().length);
	readonly styleLeftPx = computed(() => this.tabs().length * 11);

	userInfo: any;

	async ngOnInit() {
		const userStr = localStorage.getItem('currentUser');
		const adminUserData = JSON.parse(localStorage.getItem('quranera_admin_userData') || 'null');

		try {
			const childData: any = await firstValueFrom(
				this.auth.getSelfData({ collectionID: "userData", token: localStorage.getItem("accessToken") })
			);
			this.userInfo = childData?.result;

			const listsSnapshot = await runInInjectionContext(this.injector, () => {
				return firstValueFrom(this.afs.collection('activity-lists').get());
			});

			let allGames: any[] = [];

			const fetchPromises = listsSnapshot.docs.map(async (listDoc) => {
				const docId = listDoc.id;
				const docData: any = listDoc.data();

				const gamesSnapshot = await runInInjectionContext(this.injector, () => {
					return firstValueFrom(
						this.afs.collection('activity-lists').doc(docId)
							.collection('activities', ref => ref.where('type', '==', 'video')).get()
					);
				});

				const listGames = [];

				gamesSnapshot.forEach(game => {
					const gameData: any = game.data();

					if (gameData.title === 'Story Zone') {
						let url;
						let lock = false;

						if (adminUserData || this.userInfo?.activities?.[docId]?.[gameData.orderNumber]) {
							url = ['/game/activity-video', docId, gameData.orderNumber];
						} else {
							lock = true;
						}

						listGames.push({
							countryTitle: docData.countryTitle,
							orderNumber: docData.order,
							type: gameData.type,
							title: gameData.title,
							poster: `/assets/games/video/poster-${docId}-${gameData.orderNumber}.png`,
							url,
							lock
						});
					}
				});

				return listGames;
			});

			const results = await Promise.all(fetchPromises);
			results.forEach(games => {
				allGames = allGames.concat(games);
			});

			this.createArray(allGames);
		} catch (error) {
			console.error("Error loading stories data:", error);
			this.loading.set(false);
		}
	}

	private createArray(videos: any[]) {
		const generatedTabs = [];
		const count = Math.ceil(videos.length / 6);

		for (let i = 0; i < count; i++) {
			generatedTabs.push(videos.slice(i * 6, (i * 6) + 6));
		}

		this.tabs.set(generatedTabs);
		this.loading.set(false);
	}

	backToMenu() {
		this.previousState();
	}

	ngAfterViewInit(): void {
		const buttons = this.el.nativeElement.querySelectorAll('button');

		buttons.forEach((btn: HTMLButtonElement) => {
			btn.addEventListener('click', () => {
				const audio = document.getElementById('buttonClick') as HTMLAudioElement;
				if (audio) audio.play().catch(e => console.warn('Audio blocked by browser', e));
			});

			btn.addEventListener('mouseover', () => {
				const audio = document.getElementById('buttonHover') as HTMLAudioElement;
				if (audio) audio.play().catch(e => console.warn('Audio blocked by browser', e));
			});
		});
	}

	previousState() {
		this.location.back();
	}

	previous() {
		if (this.activeWindow() > 1) {
			this.activeWindow.update(val => val - 1);
		}
	}

	next() {
		if (this.activeWindow() < this.tabCount()) {
			this.activeWindow.update(val => val + 1);
		}
	}
}
